import { DatePickerWithRange } from '@/entities/datePicker'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { PageHeaderWrapper } from '@/app/layouts/PageHeaderWrapper'
import { StatusLabel, TradersIcon } from '@/shared/components'
import { useIsMobile } from '@/shared/utils'
import { getShortenId } from '@/shared/utils/getShortenId'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  ReferralWithdrawsTab,
  REFERRAL_WITHDRAWS_TAB_LABEL,
} from '@/features/referrals/ui/refDetails/withdraws/ReferralWithdrawsTab'
import {
  ReferralTopupsTab,
  REFERRAL_TOPUPS_TAB_LABEL,
} from '@/features/referrals/ui/refDetails/topups/ReferralTopupsTab'
import { ReferralStatsBanner } from '@/features/referrals/ui/refDetails/ReferralStatsBanner'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { DateRange } from 'react-day-picker'
import { getReferralStatus, getReferralStatusInfo, useReferralData } from '@/entities/referral'
import { formatPercentage, useAddToastError } from '@/shared/lib'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

export const ReferralPage = () => {
  const isMobile = useIsMobile()
  getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const params = useParams()
  const referralId = params.id as string
  const addToastError = useAddToastError()

  const [selectedTabIndex, setSelectedTabIndex] = useState(0)

  // Состояние для диапазона дат
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const today = new Date()
    return {
      from: today,
      to: today,
    }
  })

  const handleDateChange = (range: DateRange | undefined) => {
    // Гарантируем что dateRange всегда имеет значение
    if (range) {
      setDateRange(range)
    } else {
      const today = new Date()
      setDateRange({ from: today, to: today })
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Загрузка данных реферала через TanStack Query
  const { data: referralData, error } = useReferralData({
    referralId,
    dateRange: dateRange,
  })

  useEffect(() => {
    if (error) {
      addToastError(error)
    }
  }, [error, addToastError])

  // Получаем данные реферала для отображения
  const referral = referralData?.items?.[0]
  const referralName = referral?.user?.teamName || 'Нет Данных'
  const referralStatus = referral ? getReferralStatus(referral) : 'active'
  const referralPercentage = referral
    ? formatPercentage(Number(referral.referralCommission?.recipientCommission) || 0)
    : '0%'
  const subtitleClasses = 'text-text-secondary'

  // Получаем информацию о статусе для отображения
  const { title: statusTitle, color: statusLabel } = getReferralStatusInfo(referralStatus)

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      <PageHeaderWrapper
        borderTitle
        icon={
          <TradersIcon
            status={referralStatus}
            className="w-[40px] md:w-[52px] h-[40px] md:h-[52px]"
          />
        }
        title={referralName}
        subtitle={
          <div className="flex md:flex-row flex-col gap-2">
            <CopyBlock text={referralId} color="secondary" title={referralId}>
              ID: {getShortenId(referralId)}
            </CopyBlock>
            {!isMobile && (
              <span className={subtitleClasses}>Реф. процент: {referralPercentage}</span>
            )}
            <StatusLabel status={statusLabel}>{statusTitle}</StatusLabel>
          </div>
        }
        actions={
          !isMobile && (
            <DatePickerWithRange variant="outline" size="lg" onDateChange={handleDateChange} />
          )
        }
      />

      <ReferralStatsBanner
        referralId={referralId}
        dateRange={dateRange}
        onDateChange={handleDateChange}
      />

      <TabGroup selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
        <TabList className="flex gap-0.5 mb-[30px] md:mb-8 overflow-x-auto scrollbar-hide">
          <Tab className="aria-disabled:bg-transparent disabled:bg-transparent dark:aria-disabled:bg-transparent dark:disabled:bg-transparent aria-disabled:opacity-75 disabled:opacity-75 px-4 py-2 border-0 rounded-lg focus-visible:outline-0 focus:outline-none data-[hover]:ring-1 data-[selected]:ring-1 data-[selected]:ring-current focus-visible:ring-2 focus-visible:ring-primary data-[hover]:ring-inset data-[selected]:ring-inset text-[#939BA2] data-[selected]:dark:text-primary-dark data-[selected]:text-primary whitespace-nowrap aria-disabled:cursor-not-allowed disabled:cursor-not-allowed">
            {REFERRAL_WITHDRAWS_TAB_LABEL}
          </Tab>
          <Tab className="aria-disabled:bg-transparent disabled:bg-transparent dark:aria-disabled:bg-transparent dark:disabled:bg-transparent aria-disabled:opacity-75 disabled:opacity-75 px-4 py-2 border-0 rounded-lg focus-visible:outline-0 focus:outline-none data-[hover]:ring-1 data-[selected]:ring-1 data-[selected]:ring-current focus-visible:ring-2 focus-visible:ring-primary data-[hover]:ring-inset data-[selected]:ring-inset text-[#939BA2] data-[selected]:dark:text-primary-dark data-[selected]:text-primary whitespace-nowrap aria-disabled:cursor-not-allowed disabled:cursor-not-allowed">
            {REFERRAL_TOPUPS_TAB_LABEL}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ReferralWithdrawsTab referralId={referralId} />
          </TabPanel>
          <TabPanel>
            <ReferralTopupsTab referralId={referralId} dateRange={dateRange} />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  )
}
